import { ErrorMessage } from '@hookform/error-message'
import { useFormContext } from 'react-hook-form'
import { useRef, useEffect, useState, useContext } from 'react'
import { useTranslation } from 'next-i18next'
import PropTypes from 'prop-types'

// Internal components
import FormError from '../FormError/FormError'
import { UserContext } from '../../context/UserContext'

const revealClass = ' is-revealed'
const activeClass = ' is-active'

const requiredHint = ' *'

const FormItemRange = (props) => {
  const { item, onFormItemLoad, groupedState, revealState } = props

  const { register, watch, errors, getValues, setValue, formState } = useFormContext()
  const userContext = useContext(UserContext)

  // Translations
  const { t } = useTranslation(
    userContext?.userSession?.sportType?.slug ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_NAMESPACE ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_SPORT
  )
  const requiredErrorMessage = t('FORMITEM__ERROR_REQUIRED')

  // State
  const [isRequired, setIsRequired] = useState({ required: false })
  const [isGrouped, setIsGrouped] = useState(false)
  const [isLoaded, setIsLoaded] = useState(false)
  const [cachedValue, setCachedValue] = useState()

  const itemMin = item.items?.[0]
  const itemRefMin = useRef()
  const itemInputRefMin = register(itemMin.name, isRequired)

  const itemMax = item.items?.[1]
  const itemRefMax = useRef()
  const itemInputRefMax = register(itemMax.name, isRequired)

  const itemProps = item
  const itemRef = useRef()
  const itemValue = watch(item.name)

  useEffect(() => {
    if (!itemValue || isLoaded) return false
    onFormItemLoad({ itemProps, itemRef, itemValue })
    setIsLoaded(true)
  }, [itemValue])

  useEffect(() => {
    if (!item.revealed && item.required) setIsRequired({ required: requiredErrorMessage })
  }, [])

  useEffect(() => {
    if (item.revealed && item.required) setIsRequired({ required: revealState ? requiredErrorMessage : false })
  }, [revealState])

  useEffect(() => {
    if (item?.group && groupedState) {
      const itemLabel = item?.group?.label
      const groupedItem = groupedState.filter((str) => {
        return str.label.indexOf(itemLabel) >= 0
      })
      setIsGrouped(groupedItem[0].active)
    }
  }, [groupedState])

  const onInputChangeMin = (e) => {
    triggerInputChangeMin(e.target, true)
  }

  const onInputChangeMax = (e) => {
    triggerInputChangeMax(e.target, true)
  }

  const decrementFormItemMin = () => {
    const input = itemRefMin.current.querySelector('.FormItem-input')
    const min = input.dataset.min
    let value = getValues(itemMin.name)
    value--
    if (!min || value >= min) {
      setValue(itemMin.name, value)
      triggerInputChangeMin(input, true)
    }
  }

  const decrementFormItemMax = () => {
    const input = itemRefMax.current.querySelector('.FormItem-input')
    const min = input.dataset.min
    let value = getValues(itemMax.name)
    value--
    if (!min || value >= min) {
      setValue(itemMax.name, value)
      triggerInputChangeMax(input, true)
    }
  }

  const incrementFormItemMin = () => {
    const input = itemRefMin.current.querySelector('.FormItem-input')
    const max = input.dataset.max
    let value = getValues(itemMin.name)
    value++
    if (!max || value <= max) {
      setValue(itemMin.name, value)
      triggerInputChangeMin(input, true)
    }
  }

  const incrementFormItemMax = () => {
    const input = itemRefMax.current.querySelector('.FormItem-input')
    const max = input.dataset.max
    let value = getValues(itemMax.name)
    value++
    if (!max || value <= max) {
      setValue(itemMax.name, value)
      triggerInputChangeMax(input, true)
    }
  }

  const triggerInputChangeMin = (input, trigger) => {
    const validatedValue = validateInputMin(input)
    setValue(itemMin.name, validatedValue !== 0 ? validatedValue : trigger === true ? '' : 1)
    setCachedValue(input.value)

    if (getValues(itemMin.name) === getValues(itemMax.name) || getValues(itemMin.name) >= getValues(itemMax.name)) {
      const value = !isNaN(parseInt(getValues(itemMin?.name))) ? parseInt(getValues(itemMin?.name)) : 1
      setValue(itemMax.name, value)
    }
  }
  const triggerInputChangeMax = (input, trigger) => {
    const validatedValue = validateInputMax(input)
    setValue(itemMax.name, validatedValue !== 0 ? validatedValue : trigger === true ? '' : 1)
    setCachedValue(input.value)

    if (getValues(itemMax.name) === getValues(itemMin.name) || getValues(itemMax.name) <= getValues(itemMin.name)) {
      const value = !isNaN(parseInt(getValues(itemMax?.name))) ? parseInt(getValues(itemMax?.name)) : 1
      setValue(itemMin.name, value)
    }
  }

  const validateInputMin = (input) => {
    const max = input.dataset.max
    const value = getValues(itemMin.name).toString()
    if (value && parseInt(value) !== 0 && parseInt(value) <= max) {
      if ((value.match(/^(0?[1-9]|[1-9][0-9])$/) && parseInt(value) > 0) !== true) {
        return cachedValue
      } else {
        return parseInt(value)
      }
    } else return 0
  }

  const validateInputMax = (input) => {
    const max = input.dataset.max
    const value = getValues(itemMax.name).toString()
    if (value && parseInt(value) !== 0 && parseInt(value) <= max) {
      if ((value.match(/^(0?[1-9]|[1-9][0-9])$/) && parseInt(value) > 0) !== true) {
        return cachedValue
      } else {
        return parseInt(value)
      }
    } else return 0
  }

  return (
    <>
      <div ref={itemRef} className={`FormItem--${item.type}`}>
        <div
          ref={itemRefMin}
          className={`FormItem FormItem--${itemMin.type}${itemMin.modifier ? ` FormItem--${itemMin.modifier}` : ''}${
            revealState ? revealClass : ''
          }${!isGrouped ? '' : activeClass}`}
          data-reveal-target={itemMin.revealed}
        >
          <button
            className="FormItem--numberButton"
            type="button"
            tabIndex="-1"
            onClick={(e) => decrementFormItemMin(e)}
          ></button>
          {itemMin.transl && <div className={'FormItem--rangeLabel'}>{t(itemMin.transl) + requiredHint}</div>}
          <input
            type="number"
            placeholder="1"
            pattern="[0-9]*"
            data-min="1"
            data-max="99"
            defaultValue={itemMin.value}
            name={itemMin.name}
            id={itemMin.name}
            className={`FormItem-input FormItem-input--${itemMin.type}`}
            onChange={(e) => {
              itemInputRefMin.onChange(e)
              onInputChangeMin(e)
            }}
            ref={itemInputRefMin.ref}
          />
          <button
            className="FormItem--numberButton"
            type="button"
            tabIndex="-1"
            onClick={(e) => incrementFormItemMin(e)}
          ></button>
        </div>
        <div
          ref={itemRefMax}
          className={`FormItem FormItem--${itemMax.type}${itemMax.modifier ? ` FormItem--${itemMax.modifier}` : ''}${
            revealState ? revealClass : ''
          }${!isGrouped ? '' : activeClass}`}
          data-reveal-target={itemMax.revealed}
        >
          <button
            className="FormItem--numberButton"
            type="button"
            tabIndex="-1"
            onClick={(e) => decrementFormItemMax(e)}
          ></button>
          {itemMax.transl && <div className={'FormItem--rangeLabel'}>{t(itemMax.transl) + requiredHint}</div>}
          <input
            type="number"
            placeholder="1"
            pattern="[0-9]*"
            data-min="1"
            data-max="99"
            defaultValue={itemMax.value}
            name={itemMax.name}
            id={itemMax.name}
            className={`FormItem-input FormItem-input--${itemMax.type}`}
            onChange={(e) => {
              itemInputRefMax.onChange(e)
              onInputChangeMax(e)
            }}
            ref={itemInputRefMax.ref}
          />
          <button
            className="FormItem--numberButton"
            type="button"
            tabIndex="-1"
            onClick={(e) => incrementFormItemMax(e)}
          ></button>
        </div>
      </div>
      {formState.isSubmitted && <ErrorMessage errors={errors} name={item.name} as={<FormError />} />}
    </>
  )
}

FormItemRange.propTypes = {
  item: PropTypes.object,
  onFormItemLoad: PropTypes.func,
  revealState: PropTypes.any,
  groupedState: PropTypes.any
}

export default FormItemRange
